// The `BeginnerGolf` component is a React functional component designed for the Beginner Golf landing page.
// It receives `beginnerGolf` data as a prop and dynamically renders various sections, including hero content,
//  golf essentials, FAQs, and myth-busting information. The hero section features an image and introductory
//  content with a CTA button. The golf essentials section showcases key equipment and clubs, each with a title,
//  description, image, and CTA link. FAQs are displayed in an accordion format for better user interaction.
//  The myth-busting section debunks common misconceptions with a structured layout. The component ensures a
//  responsive and visually appealing design using Tailwind CSS.

import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import React, { useCallback, useEffect, useMemo } from 'react';
import { BeginnerGolfDataSource, BeginnersFAQ, GolfEssentialContent, MythBustingData } from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { searchClient } from 'algolia/searchClient';
import CategoryCarousel from './categoryCarousel';

interface BeginnerGolfProps {
  beginnerGolf: BeginnerGolfDataSource;
}

const BeginnerGolf = ({ beginnerGolf }: BeginnerGolfProps) => {
  const heroContent = beginnerGolf?.heroContent || '';
  const golfEssentialClubs = beginnerGolf?.golfEssentialClubs || '';
  const golfEssentialEquipment = beginnerGolf?.golfEssentialEquipment || '';
  const mythBusting = beginnerGolf?.mythBusting || '';
  const faqs = beginnerGolf?.faqs || '';
  const packageSets = beginnerGolf?.packageSets || '';
  const golfEssential = beginnerGolf?.golfEssentials || '';
  const [showAll, setShowAll] = useState(false);
  const visibleItems = showAll
    ? heroContent?.guideLines?.description
    : heroContent?.guideLines?.description.slice(0, 4);
  const [isExpanded, setIsExpanded] = React.useState(Array(golfEssentialEquipment?.content?.length).fill(false));
  const [Expanded, setExpanded] = React.useState(false);
  const [isExpandedItems, setExpandedItems] = React.useState(Array(golfEssentialClubs?.content?.length).fill(false));
  const handleToggle = (index: number, func: React.Dispatch<React.SetStateAction<boolean[]>>) => {
    func(
      (prev) => prev?.map((item, i) => (i === index ? !item : item)), // Toggle only the clicked index
    );
  };
  const [packageProducts, setPackageProducts] = useState<any>();
  const [essentialProducts, setEssentialProducts] = useState<any>();
  const [activePackageTab, setActivePackageTab] = useState('mens');
  const [activeEssentialTab, setActiveEssentialTab] = useState('gloves');
  const [cookies] = useCookies(['locale_selection']);

  const indexName = useMemo(
    () =>
      cookies?.locale_selection?.currency === 'GBP'
        ? process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US
        : process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE,
    [cookies?.locale_selection?.currency],
  );

  const fetchAllBeginnerGolfProducts = useCallback(async () => {
    if (!indexName) return;

    const index = searchClient.initIndex(indexName);

    const essentialQueries = golfEssential?.items?.map((item: any) => ({
      indexName,
      query: '',
      params: {
        hitsPerPage: 24,
        facetFilters: [
          item.parentCategoryName
            ? `categories.en-GB.lvl1: ${item?.parentCategoryName} > ${item?.categoryName}`
            : `categories.en-GB.lvl0: ${item?.categoryName}`,
        ],
      },
    }));

    const packageQueries = packageSets?.genders?.map((gender: any) => ({
      indexName,
      query: '',
      params: {
        hitsPerPage: 24,
        facetFilters: [
          `categories.en-GB.lvl1: ${packageSets?.parentCategoryName} > ${packageSets?.categoryName}`,
          `gender:${gender?.filterKey}`,
        ],
      },
    }));

    const queries = [...(essentialQueries || []), ...(packageQueries || [])];

    if (queries?.length > 0) {
      try {
        const { results } = await searchClient.multipleQueries(queries);
        // results will be in the same order as queries
        const essentialResults = results?.slice(0, essentialQueries?.length);
        const packageResults = results?.slice(essentialQueries?.length);

        const essentialsData: Record<string, any> = {};
        golfEssential?.items?.forEach((item: any, index: number) => {
          essentialsData[item.label.toLowerCase()] = (essentialResults?.[index] as any)?.hits || [];
        });

        const packageData: Record<string, any> = {};
        packageSets?.genders?.forEach((gender: any, index: number) => {
          packageData[gender.filterKey.toLowerCase()] = (packageResults?.[index] as any)?.hits || [];
        });

        setEssentialProducts(essentialsData);
        setPackageProducts(packageData);
      } catch (err) {
        console.error(err);
      }
    }
  }, [indexName, golfEssential, packageSets]);

  useEffect(() => {
    fetchAllBeginnerGolfProducts();
  }, []);

  const generateShopAllLink = useMemo(
    () => (category: string, subcategory: string) => {
      if (category === 'packageSets') {
        const genderData = packageSets?.genders?.find((g: any) => g.filterKey.toLowerCase() === subcategory);
        return genderData?.link || '/golf-clubs/package-sets';
      } else if (category === 'golfEssentials') {
        const essentialData = golfEssential?.items?.find((item: any) => item.label.toLowerCase() === subcategory);
        return essentialData?.link || '/golf-essentials';
      }
    },
    [packageSets, golfEssential],
  );
  return (
    <div>
      <div className="hero-content-section mt-[120px]">
        <div className="relative w-full">
          {/* Desktop Land Image */}
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.images?.landImage?.name}`}
            className="hidden w-full object-cover md:block"
          />

          {/* Mobile Land Image - Positioned Correctly */}
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.images?.mobileLandImage?.name}`}
            className="absolute bottom-0 left-0 z-0 w-full pt-10 md:hidden"
          />

          {/* Flag Image */}
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.images?.flagImage?.name}`}
            className="absolute bottom-0 left-1/2 top-[-70px] z-10 w-[350px] -translate-x-1/2 -translate-y-1/2 px-16 sm:bottom-0  sm:left-1/2 sm:top-[8%] md:w-[450px] lg:w-[500px] 2xl:w-[550px] 3xl:top-[20%] 3xl:w-[700px]"
          />

          {/* Golfer Image */}
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.images?.mainImage?.name}`}
            className="absolute bottom-0 left-[28%] top-[-43px] z-20 h-[70px] w-[70px] -translate-x-1/2 -translate-y-1/2 sm:bottom-0 sm:left-[25%] sm:top-[50%] sm:h-[90px] sm:w-[90px] md:left-[15%] md:top-[74%] lg:left-[22%] lg:top-[57%] lg:h-100 lg:w-100 3xl:left-[28%] 3xl:top-[50%] 3xl:h-200 3xl:w-200"
          />

          {/* Small Circle (Golf Ball) - Correct Placement */}
          <div className="absolute bottom-0 right-[20%] z-20 h-5 w-5 rounded-full bg-white sm:bottom-0 sm:right-[20%] sm:top-[72%] md:h-[10px] md:w-[10px]"></div>
        </div>

        <div className="sm-p-50 mt-[-10px] items-center gap-10 bg-[#53893E] p-10 pb-15">
          <div className="relative mt-50 flex w-full items-center justify-center ">
            <div className="flex flex-col items-center justify-center bg-[#53893E] px-3 py-5 text-center sm:px-30 ">
              {/* Heading */}
              <h1 className="text-wrap px-30 text-center text-[24px] font-bold normal-case leading-[1.2] text-white  md:text-[32px]">
                {heroContent?.guideLines?.heading}
              </h1>

              <div className="relative w-full max-w-sm text-center sm:w-full">
                {/* Description Box */}
                <div className="relative mt-4 w-full max-w-sm text-center">
                  <MarkDownDescription
                    markdownText={heroContent?.guideLines?.description}
                    classname={`text-white ${Expanded ? 'line-clamp-none' : 'line-clamp-3 overflow-hidden'}`}
                  />

                  {/* Gradient Overlay (Only when Collapsed) */}
                  {!Expanded && (
                    <div className="absolute bottom-0 left-0 h-full w-full bg-gradient-to-t from-[#53893E] via-[#53893E]/70 to-transparent"></div>
                  )}
                </div>

                {/* Read More / Show Less Button */}
                <div className="m-20 flex cursor-pointer items-center justify-center text-lg font-semibold text-white">
                  <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="flex items-center gap-2 text-[16px] font-bold text-white"
                  >
                    {Expanded ? 'SHOW LESS' : 'READ MORE'}
                    <span className="mx-4 flex h-17 w-17 items-center justify-center rounded-full bg-white p-2 text-center">
                      <span className="p-2 text-center text-xl text-[#272B5C]">{Expanded ? '−' : '+'}</span>
                    </span>
                  </button>
                </div>
              </div>

              <Link link={heroContent?.guideLines?.cta?.link} className="pb-10">
                <button className="group relative h-[48px] w-[200px] overflow-hidden rounded-lg bg-[#272B5C] px-18 py-5 text-[16px] font-semibold uppercase leading-7 text-white shadow-md transition-all hover:bg-[#1DCE8F] hover:text-black">
                  {/* Wrapper to contain both texts */}
                  <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold ">
                    {/* New Text (Initially Hidden, Slides In on Hover) */}
                    <span className="absolute w-full -translate-x-full text-center transition-transform duration-500 ease-in-out group-hover:translate-x-0">
                      {heroContent?.guideLines?.cta?.text}
                    </span>
                    {/* Original Text (Visible Initially, Slides Out on Hover) */}
                    <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
                      {heroContent?.guideLines?.cta?.text}
                    </span>
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="package-section my-20 flex flex-col items-center justify-center">
        <div className="flex items-center justify-center py-32">
          <h3 className="font-body text-32 font-bold uppercase leading-8 ">{'Package Sets'}</h3>
        </div>
        <div className="relative flex w-full flex-col items-center justify-center">
          <div className=" flex items-center justify-center">
            {packageSets?.genders
              ?.filter((tab) => packageProducts?.[tab.filterKey.toLowerCase()]?.length > 0)
              ?.map((tab, index) => (
                <button
                  key={index}
                  className={`relative mx-[5%] px-16 capitalize text-gray-700 transition-all duration-300 ${
                    activePackageTab === tab?.filterKey?.toLowerCase()
                      ? 'underline decoration-[#36c479] decoration-[3px] underline-offset-[8px]'
                      : 'group'
                  }`}
                  onClick={() => setActivePackageTab(tab?.filterKey?.toLowerCase())}
                >
                  <span className="block pb-4">{tab?.label}</span>
                  <div
                    className={`h-[3px] bg-[#36c479] transition-all duration-300 ${
                      activePackageTab === tab?.label?.toLowerCase()
                        ? 'w-full' // Fully visible when active
                        : 'w-0 group-hover:w-full' // Expands only on hover when inactive
                    }`}
                  ></div>
                </button>
              ))}
          </div>
          <hr className="w-full border-b border-gray-300" />
          <CategoryCarousel products={packageProducts?.[activePackageTab] || []} cookies={cookies} />
        </div>

        <Link
          link={generateShopAllLink('packageSets', activePackageTab)}
          className="group relative mt-4 h-[48px] w-180 overflow-hidden rounded-lg bg-[#1DCE8F] px-18 py-3 text-[12.8px] font-bold uppercase leading-7 text-black shadow-md transition-all hover:bg-[#272B5C] hover:text-white"
        >
          <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
            <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-0">
              Shop All {activePackageTab.charAt(0).toUpperCase() + activePackageTab.slice(1)}
            </span>
            <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
              Shop All {activePackageTab.charAt(0).toUpperCase() + activePackageTab.slice(1)}
            </span>
          </span>
        </Link>
      </div>
      <div className="golf-essenntials-club">
        <div className="golf-equipment-section mt-[120px] ">
          <div className="relative w-full ">
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${golfEssentialClubs?.image?.name}`}
              className="m-0 mb-0 hidden h-full w-full border-none p-0 leading-none md:block"
            />
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${golfEssentialClubs?.mobileImage?.name}`}
              className="w-full md:hidden"
            />
          </div>
          <div className="golf-equipment-section relative mt-[-10px]">
            <div className="flex flex-col items-center justify-center bg-[#70b23b] p-0 text-center ">
              <h1 className="mt-20 py-5 text-center font-body text-24 font-bold leading-6 text-white sm:text-32">
                {golfEssentialClubs?.overallTitle}
              </h1>
              <div className="grid h-fit w-auto justify-center gap-8 p-10  sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3 ">
                {golfEssentialClubs?.content?.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="group mx-5 flex flex-col rounded-lg bg-[#498549]  
              p-16 text-white transition-all duration-300 hover:shadow-lg sm:m-8  sm:max-w-full sm:p-5  md:m-8 md:flex-1 md:p-8 lg:items-stretch lg:justify-stretch lg:p-16"
                    >
                      {/* Title & Image */}
                      <div className="flex items-start  justify-between md:flex-col md:items-center lg:items-center">
                        <h3 className="text-[22px] font-bold">{item?.title}</h3>

                        {/* Dashed Line (Only for Large Screens) */}
                        {
                          <div className="mx-2 my-6 hidden w-full border-t-2 border-dashed border-white sm:justify-end md:block lg:block"></div>
                        }

                        <img
                          src={`https://americangolf.a.bigcontent.io/v1/static/${item?.image?.name}`}
                          className="transform:scale(2.2)
                     md:group-hover:animate-bounce(0.1) lg:object-containmb-10  mb-10
                      flex h-[60px] w-[60px] scale-110 object-contain pt-5 transition-transform sm:h-[60px] 
                      sm:w-[60px] md:m-50 md:h-[100px] md:w-[80px]  md:group-hover:translate-y-[-2%] md:group-hover:animate-bounce
                     lg:mt-25 lg:object-contain"
                          alt="Item"
                        />
                      </div>
                      {/* Dashed Line */}
                      <div className="mx-2 my-6 w-full border-t-2 border-dashed border-white md:hidden lg:hidden"></div>

                      {/* Description & Button (Collapsible Section) */}
                      <div
                        className={`relative my-15 flex flex-grow  flex-col items-start justify-start px-10 text-left font-medium
                transition-all duration-500 ease-in-out md:max-h-none md:items-center md:justify-center md:text-center md:opacity-100 lg:max-h-none lg:opacity-100
                ${
                  isExpanded[index]
                    ? 'mt-4 max-h-[500px] opacity-100'
                    : 'max-h-0 items-center overflow-hidden opacity-0 md:text-center'
                }`}
                      >
                        <p className="text-[12.8 px] md:text-[16 px] items-start justify-start p-10 text-left text-white md:items-center md:justify-center md:text-center">
                          {item?.description}
                        </p>

                        {/* CTA Button */}
                        <div className="mx-20 mt-auto flex flex-col items-start justify-start py-10 pt-4 sm:items-center sm:justify-center lg:mb-[5px] lg:mt-auto lg:items-center lg:justify-end">
                          <Link link={item?.cta?.link || ''}>
                            <button
                              className="group/button  relative mx-auto flex h-[48px] w-[150px] items-start 
            justify-start overflow-hidden rounded-lg bg-[#272B5C] px-3 
             py-3 text-left text-[12.8px] font-semibold uppercase leading-7 text-white shadow-md
                transition-all hover:bg-[#1DCE8F] hover:text-black 
                sm:text-center  lg:mt-10 "
                            >
                              {/* Wrapper to contain both texts */}
                              <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                                {/* New Text (Initially Hidden, Slides In on Hover) */}
                                <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover/button:translate-x-0">
                                  {item?.cta?.text}
                                </span>
                                {/* Original Text (Visible Initially, Slides Out on Hover) */}
                                <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover/button:translate-x-full">
                                  {item?.cta?.text}
                                </span>
                              </span>
                            </button>
                          </Link>
                        </div>
                      </div>

                      {/* Learn More / Show Less (Only for Small Screens) */}
                      <div className="top-10 flex w-full cursor-pointer items-center justify-between text-lg font-bold text-white md:hidden lg:hidden">
                        {/* Learn More / Show Less on the Left */}
                        <button
                          onClick={() => handleToggle(index, setIsExpanded)}
                          className="text-sm font-medium text-white"
                        >
                          {isExpanded[index] ? 'SHOW LESS' : 'LEARN MORE'}
                        </button>
                        {/* Plus/Minus Button on the Right */}
                        <button
                          onClick={() => handleToggle(index, setIsExpanded)}
                          className="flex h-14 w-14 items-center justify-center rounded-full bg-white p-2"
                        >
                          <span className="text-xl text-[#272B5C]">{isExpanded[index] ? '−' : '+'}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="beginners-faqs-section my-20 mt-10  lg:items-stretch lg:justify-stretch">
        <div className="flex items-center justify-center">
          <h1 className="mb-20 font-body text-[26px] font-bold capitalize leading-[1.2] text-[#272B5C]">
            {faqs?.overallTitle}
          </h1>
        </div>
        <div className="mx-auto p-16 md:max-w-[800px] lg:max-w-[600px] 3xl:max-w-[1250px] ">
          {faqs?.content?.map((item: BeginnersFAQ, index: number) => (
            <div key={index} className="mb-15 rounded-lg border-[2.5px] border-[#272B5C] bg-white shadow-sm">
              <AccordionBtn
                variant="plusAndMinus"
                className="w-full"
                closedSectionTitle={item?.question}
                sectionTileClassName="font-bold  text-[#272B5C]  text-[19px] leading-[23px] flex justify-between items-center m-0 antialiased"
                buttonClassName="w-full text-start p-16 "
                panelClassName="px-10 pb-10 pt-0 font-body text-[#272B5C] text-[12px] leading-relaxed"
              >
                <div className="text-justify font-normal">
                  <p>{item?.answer}</p>
                  {item?.cta && (
                    <Link link={item?.cta?.link || ''}>
                      <button className="group relative h-[48px] w-150 overflow-hidden rounded-lg bg-[#1DCE8F] px-18 py-3 text-[12.8px] font-bold uppercase leading-7 text-white shadow-md transition-all hover:bg-[#272b5C] hover:text-black">
                        {/* Wrapper to contain both texts */}
                        <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                          {/* New Text (Initially Hidden, Slides In on Hover) */}
                          <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-0">
                            {item?.cta?.text}
                          </span>
                          {/* Original Text (Visible Initially, Slides Out on Hover) */}
                          <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
                            {item?.cta?.text}
                          </span>
                        </span>
                      </button>
                    </Link>
                  )}
                </div>
              </AccordionBtn>
            </div>
          ))}
        </div>
        <div className="my-20 flex items-center justify-center">
          <Link link={faqs?.cta?.link || ''}>
            <button className="group relative h-[48px] w-[170px] overflow-hidden rounded-lg bg-[#1DCE8F] px-10 py-3 text-[16px] font-semibold uppercase leading-7 text-black shadow-md transition-all hover:bg-[#272B5C] hover:text-white">
              {/* Wrapper to contain both texts */}
              <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                {/* New Text (Initially Hidden, Slides In on Hover) */}
                <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-0">
                  {faqs?.cta?.text}
                </span>
                {/* Original Text (Visible Initially, Slides Out on Hover) */}
                <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
                  {faqs?.cta?.text}
                </span>
              </span>
            </button>
          </Link>
        </div>{' '}
      </div>
      <div className="golf-equipment-section my-10">
        <div className="relative w-full ">
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${golfEssentialEquipment?.image?.name}`}
            className="hidden  w-full object-cover md:block"
          />
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${golfEssentialEquipment?.mobileImage?.name}`}
            className="top-5 w-full md:hidden"
          />
        </div>
        <div className="golf-equipment-section relative mt-[-10px]">
          <div className="mt-0 flex flex-col items-center justify-center bg-[#78B376] text-center">
            <h1 className="mt-20 py-5 text-center font-body  text-24 font-bold leading-6 text-white sm:text-32">
              {golfEssentialEquipment?.overallTitle}
            </h1>
            <div className="grid h-fit w-auto items-stretch justify-center gap-8 px-8 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3">
              {golfEssentialEquipment?.content?.map((item, index) => {
                return (
                  <div
                    key={index}
                    className="group mx-5 flex flex-col rounded-lg bg-[#498549]  
                p-7 text-white transition-all duration-300 hover:shadow-lg sm:m-8  sm:max-w-full sm:p-5 md:flex-1 md:p-8 lg:items-stretch lg:justify-stretch"
                  >
                    {/* Title & Image */}
                    <div className="flex items-start  justify-between md:flex-col md:items-center lg:items-center">
                      <h3 className="text-[22px] font-bold">{item?.title}</h3>

                      {/* Dashed Line (Only for Large Screens) */}
                      {
                        <div className="mx-2 my-6 hidden w-full border-t-2 border-dashed border-white sm:justify-end md:block lg:block"></div>
                      }

                      <img
                        src={`https://americangolf.a.bigcontent.io/v1/static/${item?.image?.name}`}
                        className="transform:scale(2.2)
                       md:group-hover:animate-bounce(0.1) lg:object-containmb-10  mb-10
  flex h-[60px] w-[60px] scale-110 object-contain pt-5 transition-transform sm:h-[60px] 
  sm:w-[60px] md:m-50 md:h-[100px] md:w-[80px]  md:group-hover:translate-y-[-2%] md:group-hover:animate-bounce
                       lg:mt-25 lg:object-contain"
                        alt="Item"
                      />
                    </div>
                    {/* Dashed Line */}
                    <div className="mx-2 my-6 w-full border-t-2 border-dashed border-white md:hidden lg:hidden"></div>

                    {/* Description & Button (Collapsible Section) */}
                    <div
                      className={`sm:items-left sm:justify-left px-10relative relative my-15 flex flex-grow flex-col items-center justify-start  px-5
                  text-left font-medium transition-all duration-500 ease-in-out sm:justify-center md:max-h-none md:opacity-100 lg:max-h-none lg:opacity-100
                  ${
                    isExpandedItems[index]
                      ? 'mt-4 max-h-[500px] opacity-100'
                      : 'max-h-0 items-center overflow-hidden opacity-0 md:text-center'
                  }`}
                    >
                      <p className="sm:items-startsm:text-left text-16 text-white sm:justify-start">
                        {item?.description}
                      </p>

                      {/* CTA Button */}
                      <div className="mx-20 mt-auto flex flex-col items-start justify-start py-10 pt-4 sm:items-center sm:justify-center lg:mb-[5px] lg:mt-auto lg:items-center lg:justify-end">
                        <Link link={item?.cta?.link || ''}>
                          <button
                            className="group/button  relative mx-auto flex h-[48px] w-[120px] items-start 
            justify-start overflow-hidden rounded-lg bg-[#272B5C] px-3 
             py-3 text-left text-[12.8px] font-semibold uppercase leading-7 text-white
                shadow-md transition-all hover:bg-[#1DCE8F] 
                hover:text-black  sm:text-center lg:mt-10"
                          >
                            {/* Wrapper to contain both texts */}
                            <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                              {/* New Text (Initially Hidden, Slides In on Hover) */}
                              <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover/button:translate-x-0">
                                {item?.cta?.text}
                              </span>
                              {/* Original Text (Visible Initially, Slides Out on Hover) */}
                              <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover/button:translate-x-full">
                                {item?.cta?.text}
                              </span>
                            </span>
                          </button>
                        </Link>
                      </div>
                    </div>

                    {/* Learn More / Show Less (Only for Small Screens) */}
                    <div className="flex w-full cursor-pointer items-center justify-between text-lg font-bold text-white md:hidden lg:hidden">
                      {/* Learn More / Show Less on the Left */}
                      <button
                        onClick={() => handleToggle(index, setExpandedItems)}
                        className="text-sm font-medium text-white"
                      >
                        {isExpandedItems[index] ? 'SHOW LESS' : 'LEARN MORE'}
                      </button>
                      {/* Plus/Minus Button on the Right */}
                      <button
                        onClick={() => handleToggle(index, setExpandedItems)}
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-white p-2"
                      >
                        <span className="text-xl text-[#272B5C]">{isExpandedItems[index] ? '−' : '+'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="golf-essential my-20 flex flex-col items-center justify-center">
          <div className="flex items-center justify-center py-32">
            <h3 className="font-body text-32 font-bold uppercase leading-8 ">{'Golf Essentials'}</h3>
          </div>
          <div className=" relative flex w-full flex-col items-center justify-center">
            <div className=" flex items-center justify-center">
              {golfEssential?.items
                ?.filter((item) => essentialProducts?.[item?.label?.toLowerCase()]?.length > 0)
                ?.map((tab, index: number) => (
                  <button
                    key={index}
                    className={`relative mx-[5%] px-2  capitalize text-gray-700 transition-all duration-300 ${
                      activeEssentialTab === tab?.label?.toLowerCase()
                        ? 'underline decoration-[#36c479] decoration-[3px] underline-offset-[8px]'
                        : 'group'
                    }`}
                    onClick={() => setActiveEssentialTab(tab?.label?.toLowerCase())}
                  >
                    <span className="block pb-4">{tab?.label}</span>
                    <div
                      className={`h-[3px] bg-[#36c479] transition-all duration-300 ${
                        activePackageTab === tab?.label?.toLowerCase()
                          ? 'w-full' // Fully visible when active
                          : 'w-0 group-hover:w-full' // Expands only on hover when inactive
                      }`}
                    ></div>
                  </button>
                ))}
            </div>
            <hr className="w-full border-b border-gray-300" />
            <CategoryCarousel products={essentialProducts?.[activeEssentialTab] || []} cookies={cookies} />
            <Link
              link={generateShopAllLink('golfEssentials', activeEssentialTab)}
              className="group relative mt-4 h-[45px] w-190 overflow-hidden rounded-lg bg-[#1DCE8F] px-18 py-3 text-[12.8px] font-bold uppercase leading-7 text-black shadow-md transition-all hover:bg-[#272B5C] hover:text-white"
            >
              <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-0">
                  Shop All {activeEssentialTab.charAt(0).toUpperCase() + activeEssentialTab.slice(1)}
                </span>
                <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
                  Shop All {activeEssentialTab.charAt(0).toUpperCase() + activeEssentialTab.slice(1)}
                </span>
              </span>
            </Link>
          </div>
        </div>
        <div className="beginners-faqs-section relative my-10 ">
          <div className="  w-full">
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${mythBusting?.image?.name}`}
              className=" hidden w-full object-cover md:block"
            />
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${mythBusting?.mobileImage?.name}`}
              className="w-full md:hidden"
            />
          </div>

          <div className="relative -mt-5 flex flex-col items-center justify-center bg-[#71B23C] text-center leading-3 lg:items-stretch lg:justify-stretch">
            <div className="md:min-w-[700px] lg:mx-auto lg:w-[600px] 2xl:w-[73rem]">
              <div className="mt-50 flex items-center justify-center">
                <h1 className="mb-20 font-body text-32 font-semibold leading-7 text-white">{'MYTH BUSTING'}</h1>
              </div>
              <div className="items:start mx-auto px-15 md:min-w-[700px] lg:max-w-[600px] 3xl:max-w-[1250px]">
                {mythBusting?.content?.map((item: MythBustingData, index: number) => {
                  return (
                    <>
                      <div key={index} className="mb-15 rounded-lg bg-[#2B4E3B] text-left">
                        <AccordionBtn
                          variant="plusAndMinus"
                          className="text-19.2 p-15 !text-left font-semibold text-white "
                          closedSectionTitle={`MYTH:${item?.question}`}
                          panelClassName="text-white pt-10 text-left text-16 font-bold"
                        >
                          <div className="!text-19.2 mb-8 text-start !font-bold text-white">
                            <h2 className="mb-8 text-start text-[19.2px] font-bold text-white">BUSTED:</h2>
                          </div>
                          <div className="pt-8 text-start text-[14.4px] !font-normal leading-4 md:leading-5">
                            <p className="font-normal">{item?.answer}</p>
                            {item?.cta && (
                              <Link link={item?.cta?.link || ''}>
                                <button className="group relative my-15 h-[48px] w-150 overflow-hidden rounded-lg bg-[#1DCE8F] px-18 py-3 text-[12.8px] font-semibold uppercase leading-7 text-black shadow-md transition-all hover:bg-[#272B5C] hover:text-white">
                                  {/* Wrapper to contain both texts */}
                                  <span className="absolute inset-0 flex items-center justify-center overflow-hidden font-semibold">
                                    {/* New Text (Initially Hidden, Slides In on Hover) */}
                                    <span className="absolute w-full -translate-x-full text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-0">
                                      {item?.cta?.text}
                                    </span>
                                    {/* Original Text (Visible Initially, Slides Out on Hover) */}
                                    <span className="absolute w-full translate-x-0 text-center font-semibold transition-transform duration-500 ease-in-out group-hover:translate-x-full">
                                      {item?.cta?.text}
                                    </span>
                                  </span>
                                </button>
                              </Link>
                            )}
                          </div>
                        </AccordionBtn>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeginnerGolf;
